import React from 'react';
import { supabase } from '../lib/supabaseClient';
import { inputStyle, bigBtn, formOverlay, formContainer, tableStyle, thStyle, trStyle } from '../lib/styles';

const emptyFuel = {
  truck_number:"", driver_id:"", location_city:"", location_state:"",
  gallons:0, price_per_gallon:0, total_cost:0, odometer:0, fuel_card:"", notes:"",
};

interface FuelProps {
  data: any[];
  drivers: any[];
  companyId: string | null;
  newFuel: typeof emptyFuel;
  setNewFuel: (v: typeof emptyFuel) => void;
  showEntryForm: boolean;
  setShowEntryForm: (v: boolean) => void;
  fetchRoomData: () => void;
}

export default function Fuel({ data, drivers, companyId, newFuel, setNewFuel, showEntryForm, setShowEntryForm, fetchRoomData }: FuelProps) {

  const handleSave = async () => {
    if (!companyId) { alert("Company not loaded yet."); return; }
    if (!newFuel.truck_number.trim()) { alert("Truck number is required."); return; }
    const totalCost = newFuel.gallons * newFuel.price_per_gallon;
    const payload = { company_id:companyId, ...newFuel, total_cost:totalCost, logged_at:new Date().toISOString(), driver_id:newFuel.driver_id||null };
    const { error } = await supabase.from("fuel_logs").insert(payload);
    if (error) { alert(`Fuel log insert failed: ${error.message}`); return; }
    setShowEntryForm(false);
    setNewFuel(emptyFuel);
    fetchRoomData();
  };

  return (
    <>
      {/* ── CREATE ENTRY FORM ── */}
      {showEntryForm && (
        <div style={formOverlay}>
          <div style={{ ...formContainer, width:"580px", maxHeight:"90vh", overflowY:"auto" }}>
            <h3 style={{ color:"#32CD32", marginTop:0 }}>NEW FUEL ENTRY</h3>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="text" placeholder="Truck # *" style={inputStyle} value={newFuel.truck_number} onChange={e=>setNewFuel({...newFuel,truck_number:e.target.value})} />
              <select style={inputStyle as any} value={newFuel.driver_id} onChange={e=>setNewFuel({...newFuel,driver_id:e.target.value})}>
                <option value="">Select Driver</option>
                {drivers.map(d=><option key={d.id} value={d.id}>{d.full_name}</option>)}
              </select>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <input type="text" placeholder="Location City" style={inputStyle} value={newFuel.location_city} onChange={e=>setNewFuel({...newFuel,location_city:e.target.value})} />
              <input type="text" placeholder="Location State" style={inputStyle} value={newFuel.location_state} onChange={e=>setNewFuel({...newFuel,location_state:e.target.value})} />
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px" }}>
              <input type="number" placeholder="Gallons" style={inputStyle} value={newFuel.gallons||""} onChange={e=>setNewFuel({...newFuel,gallons:Number(e.target.value)})} />
              <input type="number" placeholder="Price / Gallon ($)" style={inputStyle} value={newFuel.price_per_gallon||""} onChange={e=>setNewFuel({...newFuel,price_per_gallon:Number(e.target.value)})} />
              <input type="number" placeholder="Odometer" style={inputStyle} value={newFuel.odometer||""} onChange={e=>setNewFuel({...newFuel,odometer:Number(e.target.value)})} />
            </div>
            <input type="text" placeholder="Fuel Card Used" style={inputStyle} value={newFuel.fuel_card} onChange={e=>setNewFuel({...newFuel,fuel_card:e.target.value})} />
            {newFuel.gallons > 0 && newFuel.price_per_gallon > 0 && (
              <div style={{ color:"#32CD32", fontSize:"12px", padding:"8px", background:"#0a0a0a", border:"1px solid #1a1a1a" }}>TOTAL COST: ${(newFuel.gallons*newFuel.price_per_gallon).toFixed(2)}</div>
            )}
            <textarea placeholder="Notes..." style={{...inputStyle,resize:"vertical"} as any} rows={2} value={newFuel.notes} onChange={e=>setNewFuel({...newFuel,notes:e.target.value})} />
            <div style={{ display:"flex", gap:"10px", marginTop:"10px" }}>
              <button onClick={handleSave} style={bigBtn}>SAVE TO SYSTEM</button>
              <button onClick={()=>setShowEntryForm(false)} style={{...bigBtn,background:"#333",color:"#fff"}}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUMMARY ── */}
      {(()=>{
        const totalGallons = data.reduce((s:number,i:any)=>s+Number(i.gallons||0),0);
        const totalCost = data.reduce((s:number,i:any)=>s+Number(i.total_cost||0),0);
        const avgPPG = totalGallons > 0 ? totalCost/totalGallons : 0;
        return (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"12px", marginBottom:"16px" }}>
            {[{label:"TOTAL GALLONS",value:totalGallons.toFixed(1)},{label:"TOTAL COST",value:`$${totalCost.toFixed(2)}`},{label:"AVG PRICE/GAL",value:`$${avgPPG.toFixed(3)}`}].map((k,i)=>(
              <div key={i} style={{ background:"#0a0a0a", border:"1px solid #1a1a1a", padding:"12px 16px" }}>
                <div style={{ color:"#555", fontSize:"9px", letterSpacing:"1px", marginBottom:"4px" }}>{k.label}</div>
                <div style={{ color:"#32CD32", fontSize:"18px", fontWeight:700 }}>{k.value}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── FUEL LOG TABLE ── */}
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>TRUCK</th>
            <th style={thStyle}>DATE</th>
            <th style={thStyle}>LOCATION</th>
            <th style={thStyle}>GALLONS</th>
            <th style={thStyle}>PPG</th>
            <th style={thStyle}>TOTAL COST</th>
            <th style={thStyle}>ODOMETER</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item,idx)=>(
            <tr key={idx} style={trStyle}>
              <td style={{ padding:"10px 12px", color:"#32CD32", fontWeight:700 }}>{item.truck_number||"---"}</td>
              <td style={{ padding:"10px 12px", color:"#aaa" }}>{item.logged_at?new Date(item.logged_at).toLocaleDateString():"---"}</td>
              <td style={{ padding:"10px 12px" }}>{item.location_city&&item.location_state?`${item.location_city}, ${item.location_state}`:"---"}</td>
              <td style={{ padding:"10px 12px" }}>{item.gallons||"---"}</td>
              <td style={{ padding:"10px 12px" }}>{item.price_per_gallon?`$${Number(item.price_per_gallon).toFixed(3)}`:"---"}</td>
              <td style={{ padding:"10px 12px", color:"#32CD32", fontWeight:700 }}>${Number(item.total_cost||0).toFixed(2)}</td>
              <td style={{ padding:"10px 12px", color:"#aaa" }}>{item.odometer?item.odometer.toLocaleString():"---"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
